import { httpResource } from '@angular/common/http';
import { Component, computed, inject, linkedSignal, model, output } from '@angular/core';
import {
  DmAccordion,
  DmAccordionContent,
  DmAccordionItem,
  DmAccordionTrigger,
  DmButton,
  DmDrawer,
  DmDrawerFooter,
  DmDrawerHeader,
  DmIcon,
  DmInput,
  DmLoadingIndicator,
  DmRichtext,
} from '@digitale-menschen/component-library-angular';
import { API_CONFIGURATION, environmentUser } from '@lexplus-environments';
import { faqControllerFindAllByCategoryV1, LexplusFaqCategoryDto } from '@lexplus-user-api';
import { EmptyStateComponent, resolveDocumentName, resolveHttpLink } from '@lexplus-shared-lib';

/** The FAQ drawer, opened from the sidebar over whichever page is on screen. */
@Component({
  selector: 'lp-faq-drawer',
  imports: [
    DmDrawer,
    DmDrawerHeader,
    DmDrawerFooter,
    DmButton,
    DmAccordion,
    DmAccordionItem,
    DmAccordionTrigger,
    DmAccordionContent,
    DmIcon,
    DmInput,
    DmLoadingIndicator,
    DmRichtext,
    EmptyStateComponent,
  ],
  templateUrl: './faq-drawer.component.html',
  styleUrl: './faq-drawer.component.scss',
})
export class FaqDrawerComponent {
  private readonly apiConfiguration = inject(API_CONFIGURATION);

  public readonly isOpen = model<boolean>(false);

  /** Asks the host to open the contact dialog; the host decides what happens to the drawer. */
  public readonly contactRequested = output<void>();

  /** Read on first open only, so a page that never shows the drawer never asks for the FAQ. */
  private readonly categoriesResource = httpResource<LexplusFaqCategoryDto[]>(() =>
    this.isOpen() ? `${this.apiConfiguration.baseUrlUser}${faqControllerFindAllByCategoryV1.PATH}` : undefined,
  );

  /**
   * The one open question across every category, since each category is its own accordion.
   * Keyed by category and FAQ together: the same FAQ can sit in several categories, so `faqID`
   * alone would open every copy at once.
   */
  protected readonly openItemID = linkedSignal<boolean, string | null>({ source: this.isOpen, computation: () => null });

  protected readonly isLoading = computed<boolean>(() => this.categoriesResource.isLoading());
  protected readonly hasError = computed<boolean>(() => this.categoriesResource.status() === 'error');

  /** Cleared whenever the drawer opens or closes, so a reopened drawer shows the whole FAQ. */
  protected readonly searchTerm = linkedSignal<boolean, string>({ source: this.isOpen, computation: () => '' });

  private readonly allCategories = computed<LexplusFaqCategoryDto[]>(() =>
    this.categoriesResource.hasValue() ? this.categoriesResource.value() : [],
  );

  /**
   * Lower-cased plain text of every question's title and answer, built once per load rather than per keystroke.
   * Keyed like the accordion items: the same FAQ can sit in several categories, each with its own title and text.
   */
  private readonly searchIndex = computed<Map<string, string>>(() => {
    const index = new Map<string, string>();
    for (const category of this.allCategories()) {
      for (const faq of category.faqs) {
        const plainText = new DOMParser().parseFromString(faq.text.replace(/<[^>]*>/g, ' '), 'text/html').body.textContent ?? '';
        index.set(this.buildItemID(category.faqKategorieID, faq.faqID), `${faq.title} ${plainText}`.toLowerCase());
      }
    }
    return index;
  });

  /** Questions matching the search in title or text; categories left without a match drop out. */
  protected readonly categories = computed<LexplusFaqCategoryDto[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) {
      return this.allCategories();
    }
    return this.allCategories()
      .map(
        (category): LexplusFaqCategoryDto => ({
          ...category,
          faqs: category.faqs.filter((faq) => this.searchIndex().get(this.buildItemID(category.faqKategorieID, faq.faqID))?.includes(term)),
        }),
      )
      .filter((category) => category.faqs.length > 0);
  });

  protected readonly environmentUser = environmentUser;
  protected readonly searchPlaceholder = $localize`:@@common.search.placeholder:Suche`;
  protected readonly noResultsTitle = $localize`:@@faq.drawer.noResults:Keine Fragen gefunden`;
  protected readonly loadErrorTitle = $localize`:@@common.fragen.ladefehler:Fragen konnten nicht geladen werden`;

  /** Turns an item's `source` into a link target; `undefined` where it is a note rather than an address. */
  protected readonly resolveHttpLink = resolveHttpLink;

  /** Names an item's document: its stored name, or the file name its address ends in. */
  protected readonly resolveDocumentName = resolveDocumentName;

  protected readonly drawerLabel = $localize`:@@page.faq.heading:FAQ`;
  protected readonly supportingText = $localize`:@@faq.drawer.supportingText:LEXPLUS - Häufige Fragen`;

  /** Requests the FAQ again after it failed to load. */
  protected retryLoading(): void {
    this.categoriesResource.reload();
  }

  protected buildAccordionID(categoryID: number): string {
    return `lp-faq-accordion-${categoryID}`;
  }

  protected buildItemID(categoryID: number, faqID: number): string {
    return `lp-faq-item-${categoryID}-${faqID}`;
  }

  protected onItemToggle(itemID: string, open: boolean): void {
    if (open) {
      this.openItemID.set(itemID);
    } else if (this.openItemID() === itemID) {
      this.openItemID.set(null);
    }
  }
}
